import { Component } from "@theme/component";

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  /** @type {HTMLElement | null} */
  #slidesContainer = null;
  #animationDuration = 0;
  #scrollDistance = 0;
  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    this.#slidesContainer = this.querySelector(".announcement-bar__slides");
    if (!this.#slidesContainer || !this.autoplay) return;

    if (this.#slidesContainer) {
      this.#slidesContainer.classList.remove("scrolling");
      this.#slidesContainer.style.transform = "";
    }

    // Observe attribute changes for scroll-direction
    this.#observer = new MutationObserver(() => {
      if (this.#slidesContainer && this.autoplay) {
        this.play();
      }
    });
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ["scroll-direction"],
    });

    requestAnimationFrame(() => {
      this.#duplicateSlides();

      requestAnimationFrame(() => {
        this.#calculateAnimationDuration();
        requestAnimationFrame(() => {
          if (!document.hidden) {
            this.play();
          }
        });
      });
    });

    document.addEventListener("visibilitychange", this.#handleVisibilityChange);

    window.addEventListener("pagereveal", this.#handlePageReveal, {
      once: true,
    });
  }

  disconnectedCallback() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    document.removeEventListener(
      "visibilitychange",
      this.#handleVisibilityChange
    );
    window.removeEventListener("pagereveal", this.#handlePageReveal);
    super.disconnectedCallback();
  }

  /**
   * Duplicates slides for seamless infinite scroll
   */
  #duplicateSlides() {
    if (!this.#slidesContainer) return;

    const slides = Array.from(this.#slidesContainer.children);
    if (slides.length === 0) return;

    // Check if slides are already duplicated by checking for data-duplicate attribute
    const hasDuplicates = slides.some(
      (slide) =>
        slide instanceof Element && slide.hasAttribute("data-duplicate")
    );

    if (hasDuplicates) {
      slides.forEach((slide) => {
        if (slide instanceof Element && slide.hasAttribute("data-duplicate")) {
          slide.remove();
        }
      });
      const remainingSlides = Array.from(this.#slidesContainer.children);
      remainingSlides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        if (clone instanceof Element) {
          clone.setAttribute("data-duplicate", "");
        }
        if (this.#slidesContainer) {
          this.#slidesContainer.appendChild(clone);
        }
      });
    } else {
      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        if (clone instanceof Element) {
          clone.setAttribute("data-duplicate", "");
        }
        if (this.#slidesContainer) {
          this.#slidesContainer.appendChild(clone);
        }
      });
    }
  }

  /**
   * Calculates animation duration and scroll distance based on content width and speed setting
   */
  #calculateAnimationDuration() {
    if (!this.#slidesContainer) return;

    // After duplication, scrollWidth includes both sets of slides
    // Divide by 2 to get the exact width of one set (original slides)
    const totalScrollWidth = this.#slidesContainer.scrollWidth;
    this.#scrollDistance = totalScrollWidth / 2;

    // The speed setting is in seconds per cycle
    const interval = this.autoplayInterval;
    if (interval) {
      this.#animationDuration = interval / 1000;
    }
  }

  /**
   * Gets the scroll direction from the component attribute
   */
  get scrollDirection() {
    return this.getAttribute("scroll-direction") || "left";
  }

  /**
   * Starts automatic scrolling animation.
   */
  play() {
    if (!this.autoplay || !this.#slidesContainer) return;

    this.paused = false;
    const direction = this.scrollDirection;
    const scrollDistance =
      direction === "right"
        ? `${this.#scrollDistance}px`
        : `-${this.#scrollDistance}px`;

    this.#slidesContainer.classList.remove("scrolling");
    this.#slidesContainer.style.transform = "";

    void this.#slidesContainer.offsetWidth;

    this.#slidesContainer.style.setProperty(
      "--animation-duration",
      `${this.#animationDuration}s`
    );
    this.#slidesContainer.style.setProperty(
      "--scroll-distance",
      scrollDistance
    );
    this.#slidesContainer.setAttribute("data-direction", direction);

    // For right direction, set initial transform to show duplicated content
    if (direction === "right") {
      this.#slidesContainer.style.transform = `translateX(-${
        this.#scrollDistance
      }px)`;
    } else {
      this.#slidesContainer.style.transform = "translateX(0)";
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.#slidesContainer && !this.paused) {
          this.#slidesContainer.classList.add("scrolling");
        }
      });
    });
  }

  /**
   * Pauses automatic scrolling animation.
   */
  pause() {
    if (!this.#slidesContainer) return;

    this.paused = true;
    const computedStyle = window.getComputedStyle(this.#slidesContainer);
    const currentTransform = computedStyle.transform;

    this.#slidesContainer.classList.remove("scrolling");

    if (currentTransform && currentTransform !== "none") {
      this.#slidesContainer.style.transform = currentTransform;
    }
  }

  /**
   * Resumes automatic scrolling animation if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || !this.#slidesContainer) return;

    if (!this.paused) return;

    this.#calculateAnimationDuration();

    void this.#slidesContainer.offsetWidth;

    this.play();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(paused) {
    this.toggleAttribute("paused", paused);
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute("autoplay");
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * Pause the scrolling when the page is hidden.
   */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      if (this.autoplay && this.#slidesContainer) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!document.hidden && this.isConnected && this.paused) {
              this.resume();
            }
          });
        });
      }
    }
  };

  /**
   * Handle page reveal event (used by view transitions)
   */
  #handlePageReveal = () => {
    if (this.#slidesContainer && this.autoplay) {
      requestAnimationFrame(() => {
        this.#duplicateSlides();
        requestAnimationFrame(() => {
          this.#calculateAnimationDuration();
          requestAnimationFrame(() => {
            if (!document.hidden) {
              this.play();
            }
          });
        });
      });
    }
  };
}

if (!customElements.get("announcement-bar-component")) {
  customElements.define("announcement-bar-component", AnnouncementBar);
}
